import React, { useEffect, useState, useMemo } from "react";
import { useMsal } from "@azure/msal-react";
import DataTable, { TableColumn } from "react-data-table-component";
import {
    FileEdit,
    Users,
    Clock,
    AlertTriangle,
    Download,
    Search,
    Calendar,
    Building2,
    TrendingUp,
} from "lucide-react";
import * as XLSX from "xlsx";
import { getApiBaseUrl } from "../utils/baseUrl";
import { useLoading, acquireTokenSafe } from "../App";
import { Department, TaskEntry } from "../types";

// ── Helpers ──────────────────────────────────────────────────

const secToHours = (sec: number) => Math.round((sec / 3600) * 100) / 100;

const getStartOfWeek = (d: Date): Date => {
    const day = d.getDay();
    const diff = d.getDate() - day + (day === 0 ? -6 : 1);
    return new Date(d.getFullYear(), d.getMonth(), diff);
};

const formatDateInput = (d: Date): string => d.toISOString().split("T")[0];

type DatePreset = "week" | "30days" | "ytd" | "custom";

const getDatesForPreset = (preset: DatePreset): { start: string; end: string } => {
    const now = new Date();
    const end = formatDateInput(now);
    switch (preset) {
        case "week": {
            const monday = getStartOfWeek(now);
            return { start: formatDateInput(monday), end };
        }
        case "30days": {
            const d = new Date(now);
            d.setDate(d.getDate() - 30);
            return { start: formatDateInput(d), end };
        }
        case "ytd":
            return { start: formatDateInput(new Date(now.getFullYear(), 0, 1)), end };
        default:
            return { start: formatDateInput(new Date(now.getFullYear(), now.getMonth(), 1)), end };
    }
};

// ── Types ────────────────────────────────────────────────────

interface DraftUser {
    userId: number;
    fullName: string;
    email: string;
    department: string;
    departmentId: number | null;
    draftHours: number;
    billableDraftHours: number;
    nonBillableDraftHours: number;
    entryCount: number;
    oldestDraft: string | null;
    newestDraft: string | null;
    avgDraftAge: number; // days
    entries: TaskEntry[];
}

// ── Custom table styles ─────────────────────────────────────

const customStyles = {
    table: { style: { backgroundColor: "var(--bg-secondary)", borderRadius: "8px" } },
    headRow: {
        style: {
            backgroundColor: "var(--bg-tertiary)",
            borderBottomColor: "var(--border-color)",
            color: "var(--text-primary)",
            fontWeight: 600,
            fontSize: "0.85rem",
        },
    },
    headCells: { style: { color: "var(--text-secondary)", paddingLeft: "16px", paddingRight: "16px" } },
    rows: {
        style: {
            backgroundColor: "var(--bg-secondary)",
            color: "var(--text-primary)",
            borderBottomColor: "var(--border-color)",
        },
        highlightOnHoverStyle: {
            backgroundColor: "var(--bg-hover)",
            color: "var(--text-primary)",
            borderBottomColor: "var(--border-color)",
        },
    },
    cells: { style: { color: "var(--text-primary)", paddingLeft: "16px", paddingRight: "16px" } },
    pagination: {
        style: {
            backgroundColor: "var(--bg-secondary)",
            color: "var(--text-secondary)",
            borderTopColor: "var(--border-color)",
        },
        pageButtonsStyle: {
            color: "var(--text-secondary)",
            fill: "var(--text-secondary)",
            "&:hover:not(:disabled)": { backgroundColor: "var(--bg-hover)" },
            "&:disabled": { opacity: 0.4 },
        },
    },
    expanderRow: {
        style: { backgroundColor: "var(--bg-secondary)", color: "var(--text-primary)" },
    },
    noData: {
        style: { backgroundColor: "var(--bg-secondary)", color: "var(--text-secondary)" },
    },
};

// ── Expandable Row ──────────────────────────────────────────

const ExpandedEntries: React.FC<{ data: DraftUser }> = ({ data }) => (
    <div style={{ padding: "12px 24px" }}>
        <table className="data-table" style={{ width: "100%" }}>
            <thead>
                <tr>
                    <th>Date</th>
                    <th>Task</th>
                    <th>Client</th>
                    <th>Hours</th>
                    <th>Billable</th>
                    <th>Age (days)</th>
                    <th>Comment</th>
                </tr>
            </thead>
            <tbody>
                {data.entries.map((entry) => {
                    const ageDays = Math.floor(
                        (Date.now() - new Date(entry.start_time).getTime()) / (1000 * 60 * 60 * 24)
                    );
                    return (
                        <tr key={entry.id}>
                            <td className="cell-secondary">
                                {new Date(entry.start_time).toLocaleDateString("en-IE")}
                            </td>
                            <td className="cell-primary">{entry.task_title || "—"}</td>
                            <td className="cell-secondary">{entry.business_name || "—"}</td>
                            <td className="cell-value">{secToHours(entry.duration).toFixed(2)}</td>
                            <td>
                                <span className={`status-badge ${entry.task_billable ? "active" : "inactive"}`}>
                                    {entry.task_billable ? "Billable" : "Non-Billable"}
                                </span>
                            </td>
                            <td>
                                <span
                                    style={{
                                        fontWeight: 600,
                                        color:
                                            ageDays > 14
                                                ? "var(--status-error, #d9534f)"
                                                : ageDays > 7
                                                ? "var(--status-warning, #f0ad4e)"
                                                : "var(--text-secondary)",
                                    }}
                                >
                                    {ageDays}d
                                </span>
                            </td>
                            <td
                                className="cell-secondary"
                                style={{ maxWidth: 300, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
                            >
                                {entry.comment || "—"}
                            </td>
                        </tr>
                    );
                })}
                {data.entries.length === 0 && (
                    <tr>
                        <td colSpan={7} style={{ textAlign: "center", padding: 16, color: "var(--text-muted)" }}>
                            No draft entries
                        </td>
                    </tr>
                )}
            </tbody>
        </table>
    </div>
);

// ── Main Component ──────────────────────────────────────────

const DraftTimekeeping: React.FC = () => {
    const { instance, accounts } = useMsal();
    const { setLoading } = useLoading();

    const [departments, setDepartments] = useState<Department[]>([]);
    const [rawEntries, setRawEntries] = useState<any[]>([]);
    const [error, setError] = useState<string | null>(null);

    const [dateRange, setDateRange] = useState<DatePreset>("30days");
    const [startDate, setStartDate] = useState(() => getDatesForPreset("30days").start);
    const [endDate, setEndDate] = useState(() => getDatesForPreset("30days").end);
    const [selectedDepartment, setSelectedDepartment] = useState<string>("all");
    const [search, setSearch] = useState("");

    const handlePresetChange = (preset: DatePreset) => {
        setDateRange(preset);
        if (preset !== "custom") {
            const { start, end } = getDatesForPreset(preset);
            setStartDate(start);
            setEndDate(end);
        }
    };

    const getToken = async () => acquireTokenSafe(instance, accounts);

    // Fetch departments
    useEffect(() => {
        const fetchDepartments = async () => {
            const token = await getToken();
            if (!token) return;
            try {
                const res = await fetch(`${getApiBaseUrl()}/api/enhanced-timekeeping/departments`, {
                    headers: { Authorization: `Bearer ${token}` },
                });
                if (!res.ok) throw new Error("Failed to fetch departments");
                setDepartments(await res.json());
            } catch (err: any) {
                console.error("Dept fetch error:", err);
            }
        };
        fetchDepartments();
    }, [accounts]); // eslint-disable-line

    // Fetch draft entries
    useEffect(() => {
        const fetchEntries = async () => {
            const token = await getToken();
            if (!token) return;
            setLoading(true);
            setError(null);
            try {
                const url = `${getApiBaseUrl()}/api/enhanced-timekeeping/draft-data?startDate=${encodeURIComponent(startDate)}&endDate=${encodeURIComponent(endDate)}`;
                const res = await fetch(url, {
                    headers: { Authorization: `Bearer ${token}` },
                });
                if (!res.ok) throw new Error("Failed to fetch draft timekeeping data");
                setRawEntries(await res.json());
            } catch (err: any) {
                console.error("Draft fetch error:", err);
                setError(err.message || "Failed to load data");
            } finally {
                setLoading(false);
            }
        };
        if (startDate && endDate) fetchEntries();
    }, [startDate, endDate, accounts]); // eslint-disable-line

    // Aggregate per-user
    const userRows: DraftUser[] = useMemo(() => {
        const now = Date.now();
        const map = new Map<number, DraftUser>();

        for (const row of rawEntries) {
            const uid = Number(row.admin_user_id);
            if (!map.has(uid)) {
                map.set(uid, {
                    userId: uid,
                    fullName: `${row.first_name || ""} ${row.last_name || ""}`.trim(),
                    email: row.email || "",
                    department: row.department_title || "Unassigned",
                    departmentId: row.department_id ? Number(row.department_id) : null,
                    draftHours: 0,
                    billableDraftHours: 0,
                    nonBillableDraftHours: 0,
                    entryCount: 0,
                    oldestDraft: null,
                    newestDraft: null,
                    avgDraftAge: 0,
                    entries: [],
                });
            }
            const user = map.get(uid)!;
            const hours = secToHours(row.duration || 0);
            user.draftHours += hours;
            if (row.task_billable) user.billableDraftHours += hours;
            else user.nonBillableDraftHours += hours;
            user.entryCount += 1;

            const entryDate = row.start_time;
            if (!user.oldestDraft || entryDate < user.oldestDraft) user.oldestDraft = entryDate;
            if (!user.newestDraft || entryDate > user.newestDraft) user.newestDraft = entryDate;

            user.entries.push({
                id: Number(row.id),
                start_time: row.start_time,
                end_time: row.end_time,
                duration: row.duration || 0,
                comment: row.comment,
                business_id: row.business_id,
                task_type_id: row.task_type_id,
                admin_user_id: uid,
                status: row.entry_status,
                rate: row.rate ? Number(row.rate) : null,
                core_service_id: row.core_service_id ? Number(row.core_service_id) : null,
                task_title: row.task_title,
                task_billable: row.task_billable,
                business_name: row.business_name,
            });
        }

        for (const user of map.values()) {
            user.draftHours = Math.round(user.draftHours * 100) / 100;
            user.billableDraftHours = Math.round(user.billableDraftHours * 100) / 100;
            user.nonBillableDraftHours = Math.round(user.nonBillableDraftHours * 100) / 100;
            // Average age: average days since each entry's start_time
            if (user.entries.length > 0) {
                const totalAge = user.entries.reduce(
                    (sum, e) => sum + (now - new Date(e.start_time).getTime()) / (1000 * 60 * 60 * 24),
                    0
                );
                user.avgDraftAge = Math.round(totalAge / user.entries.length);
            }
        }

        return Array.from(map.values()).sort((a, b) => b.draftHours - a.draftHours);
    }, [rawEntries]);

    // Filters
    const filteredData = useMemo(() => {
        let data = userRows;
        if (selectedDepartment !== "all") {
            data = data.filter((u) => String(u.departmentId) === selectedDepartment);
        }
        if (search) {
            const s = search.toLowerCase();
            data = data.filter(
                (u) =>
                    u.fullName.toLowerCase().includes(s) ||
                    u.email.toLowerCase().includes(s) ||
                    u.department.toLowerCase().includes(s)
            );
        }
        return data;
    }, [userRows, selectedDepartment, search]);

    // Department breakdown
    const deptSummaries = useMemo(() => {
        const map = new Map<
            string,
            { department: string; headcount: number; draftHours: number; billableHours: number; entryCount: number }
        >();
        for (const u of filteredData) {
            const key = u.department;
            if (!map.has(key)) {
                map.set(key, { department: key, headcount: 0, draftHours: 0, billableHours: 0, entryCount: 0 });
            }
            const s = map.get(key)!;
            s.headcount += 1;
            s.draftHours += u.draftHours;
            s.billableHours += u.billableDraftHours;
            s.entryCount += u.entryCount;
        }
        for (const s of map.values()) {
            s.draftHours = Math.round(s.draftHours * 100) / 100;
            s.billableHours = Math.round(s.billableHours * 100) / 100;
        }
        return Array.from(map.values()).sort((a, b) => b.draftHours - a.draftHours);
    }, [filteredData]);

    // Totals
    const totals = useMemo(() => {
        const t = filteredData.reduce(
            (acc, u) => ({
                headcount: acc.headcount + 1,
                draftHours: acc.draftHours + u.draftHours,
                billableHours: acc.billableHours + u.billableDraftHours,
                nonBillableHours: acc.nonBillableHours + u.nonBillableDraftHours,
                entryCount: acc.entryCount + u.entryCount,
            }),
            { headcount: 0, draftHours: 0, billableHours: 0, nonBillableHours: 0, entryCount: 0 }
        );
        // Users with entries older than 7 days
        const staleUsers = filteredData.filter((u) => u.avgDraftAge > 7).length;
        return {
            ...t,
            draftHours: Math.round(t.draftHours * 100) / 100,
            billableHours: Math.round(t.billableHours * 100) / 100,
            nonBillableHours: Math.round(t.nonBillableHours * 100) / 100,
            staleUsers,
        };
    }, [filteredData]);

    // Age colour helper
    const ageColor = (days: number): string => {
        if (days > 14) return "var(--status-error, #d9534f)";
        if (days > 7) return "var(--status-warning, #f0ad4e)";
        return "var(--status-success, #5cb85c)";
    };

    // Table columns
    const columns: TableColumn<DraftUser>[] = [
        {
            name: "Name",
            selector: (row) => row.fullName,
            sortable: true,
            grow: 2,
            cell: (row) => <span className="cell-primary">{row.fullName}</span>,
        },
        {
            name: "Department",
            selector: (row) => row.department,
            sortable: true,
            cell: (row) => <span className="cell-secondary">{row.department}</span>,
        },
        {
            name: "Draft Hours",
            selector: (row) => row.draftHours,
            sortable: true,
            right: true,
            cell: (row) => <span className="cell-value" style={{ fontWeight: 600 }}>{row.draftHours.toFixed(2)}</span>,
        },
        {
            name: "Billable",
            selector: (row) => row.billableDraftHours,
            sortable: true,
            right: true,
            cell: (row) => <span className="cell-value accent">{row.billableDraftHours.toFixed(2)}</span>,
        },
        {
            name: "Non-Billable",
            selector: (row) => row.nonBillableDraftHours,
            sortable: true,
            right: true,
            cell: (row) => <span className="cell-secondary">{row.nonBillableDraftHours.toFixed(2)}</span>,
        },
        {
            name: "Entries",
            selector: (row) => row.entryCount,
            sortable: true,
            right: true,
            compact: true,
            cell: (row) => <span className="cell-secondary">{row.entryCount}</span>,
        },
        {
            name: "Avg Age",
            selector: (row) => row.avgDraftAge,
            sortable: true,
            right: true,
            cell: (row) => (
                <span style={{ fontWeight: 600, color: ageColor(row.avgDraftAge) }}>
                    {row.avgDraftAge}d
                </span>
            ),
        },
        {
            name: "Oldest Draft",
            selector: (row) => row.oldestDraft || "",
            sortable: true,
            cell: (row) =>
                row.oldestDraft ? (
                    <span className="cell-secondary">
                        {new Date(row.oldestDraft).toLocaleDateString("en-IE")}
                    </span>
                ) : (
                    <span className="cell-secondary">—</span>
                ),
        },
    ];

    // Export CSV
    const downloadCSV = () => {
        const headers = [
            "Full Name",
            "Email",
            "Department",
            "Draft Hours",
            "Billable Draft Hours",
            "Non-Billable Draft Hours",
            "Entry Count",
            "Avg Age (days)",
            "Oldest Draft",
        ];
        const rows = filteredData
            .map((r) =>
                [
                    `"${r.fullName.replace(/"/g, '""')}"`,
                    r.email,
                    `"${r.department.replace(/"/g, '""')}"`,
                    r.draftHours.toFixed(2),
                    r.billableDraftHours.toFixed(2),
                    r.nonBillableDraftHours.toFixed(2),
                    r.entryCount,
                    r.avgDraftAge,
                    r.oldestDraft ? new Date(r.oldestDraft).toLocaleDateString("en-IE") : "",
                ].join(",")
            )
            .join("\n");
        const blob = new Blob([`${headers.join(",")}\n${rows}`], { type: "text/csv;charset=utf-8;" });
        const link = document.createElement("a");
        link.href = URL.createObjectURL(blob);
        link.download = `draft_time_report_${startDate}_${endDate}.csv`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    // Export XLSX
    const downloadXLSX = () => {
        const wb = XLSX.utils.book_new();

        const deptData = deptSummaries.map((d) => ({
            Department: d.department,
            Headcount: d.headcount,
            "Draft Hours": d.draftHours,
            "Billable Draft Hours": d.billableHours,
            "Entry Count": d.entryCount,
        }));
        XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(deptData), "Department Summary");

        const userData = filteredData.map((u) => ({
            "Full Name": u.fullName,
            Email: u.email,
            Department: u.department,
            "Draft Hours": u.draftHours,
            "Billable Draft Hours": u.billableDraftHours,
            "Non-Billable Draft Hours": u.nonBillableDraftHours,
            "Entry Count": u.entryCount,
            "Avg Age (days)": u.avgDraftAge,
            "Oldest Draft": u.oldestDraft ? new Date(u.oldestDraft).toLocaleDateString("en-IE") : "",
        }));
        XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(userData), "User Detail");

        XLSX.writeFile(wb, `draft_time_report_${startDate}_${endDate}.xlsx`);
    };

    return (
        <div className="page-container">
            <div className="page-header compact">
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                    <div className="page-title">
                        <FileEdit size={20} />
                        <h1>Draft Time Report</h1>
                    </div>
                    <div style={{ display: "flex", gap: 8 }}>
                        <button className="download-btn" onClick={downloadCSV} title="Export as CSV">
                            <Download size={16} /> CSV
                        </button>
                        <button className="download-btn" onClick={downloadXLSX} title="Export as XLSX">
                            <Download size={16} /> XLSX
                        </button>
                    </div>
                </div>
            </div>

            {/* Date Presets + Filters */}
            <div className="table-controls" style={{ marginBottom: 20 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                    {(["week", "30days", "ytd", "custom"] as DatePreset[]).map((preset) => (
                        <button
                            key={preset}
                            className={`filter-select${dateRange === preset ? " active" : ""}`}
                            style={{
                                cursor: "pointer",
                                padding: "6px 12px",
                                border: dateRange === preset ? "1px solid var(--accent-primary)" : "1px solid var(--border-color)",
                                borderRadius: 6,
                                background: dateRange === preset ? "var(--accent-primary)" : "var(--bg-secondary)",
                                color: dateRange === preset ? "#fff" : "var(--text-secondary)",
                                fontWeight: dateRange === preset ? 600 : 400,
                                fontSize: 13,
                            }}
                            onClick={() => handlePresetChange(preset)}
                        >
                            {preset === "week" ? "Past Week" : preset === "30days" ? "Last 30 Days" : preset === "ytd" ? "YTD" : "Custom"}
                        </button>
                    ))}
                </div>

                {dateRange === "custom" && (
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                        <Calendar size={16} style={{ color: "var(--text-muted)" }} />
                        <input type="date" className="search-input" style={{ minWidth: 150 }} value={startDate} onChange={(e) => setStartDate(e.target.value)} />
                        <span style={{ color: "var(--text-muted)" }}>to</span>
                        <input type="date" className="search-input" style={{ minWidth: 150 }} value={endDate} onChange={(e) => setEndDate(e.target.value)} />
                    </div>
                )}

                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <Building2 size={16} style={{ color: "var(--text-muted)" }} />
                    <select className="filter-select" value={selectedDepartment} onChange={(e) => setSelectedDepartment(e.target.value)}>
                        <option value="all">All Departments</option>
                        {departments.map((d) => (
                            <option key={d.id} value={String(d.id)}>{d.title}</option>
                        ))}
                    </select>
                </div>

                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <Search size={16} style={{ color: "var(--text-muted)" }} />
                    <input
                        type="text"
                        className="search-input"
                        placeholder="Search by name, email, or department..."
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                    />
                </div>
            </div>

            {error && (
                <div style={{ padding: "12px 16px", marginBottom: 16, borderRadius: 8, background: "rgba(217,83,79,0.1)", color: "var(--status-error, #d9534f)", border: "1px solid var(--status-error, #d9534f)" }}>
                    {error}
                </div>
            )}

            {/* Summary Cards */}
            <div className="stats-grid" style={{ marginBottom: 24 }}>
                <div className="stat-card">
                    <div className="stat-icon blue">
                        <Users size={20} />
                    </div>
                    <div className="stat-content">
                        <div className="stat-value">{totals.headcount}</div>
                        <div className="stat-label">People with Drafts</div>
                    </div>
                </div>
                <div className="stat-card">
                    <div className="stat-icon purple">
                        <Clock size={20} />
                    </div>
                    <div className="stat-content">
                        <div className="stat-value">{totals.draftHours.toFixed(1)}</div>
                        <div className="stat-label">Total Draft Hours</div>
                    </div>
                </div>
                <div className="stat-card">
                    <div className="stat-icon green">
                        <TrendingUp size={20} />
                    </div>
                    <div className="stat-content">
                        <div className="stat-value">{totals.billableHours.toFixed(1)}</div>
                        <div className="stat-label">Billable (Draft)</div>
                    </div>
                </div>
                <div className="stat-card">
                    <div className="stat-icon orange">
                        <FileEdit size={20} />
                    </div>
                    <div className="stat-content">
                        <div className="stat-value">{totals.entryCount}</div>
                        <div className="stat-label">Draft Entries</div>
                    </div>
                </div>
                <div className="stat-card highlight">
                    <div className="stat-icon yellow">
                        <AlertTriangle size={20} />
                    </div>
                    <div className="stat-content">
                        <div className="stat-value">{totals.staleUsers}</div>
                        <div className="stat-label">Stale (&gt;7d avg)</div>
                    </div>
                </div>
            </div>

            {/* Department Breakdown */}
            {selectedDepartment === "all" && deptSummaries.length > 1 && (
                <div className="data-table-container" style={{ marginBottom: 24 }}>
                    <h3 style={{ margin: "0 0 12px", color: "var(--text-primary)", fontSize: 16, fontWeight: 600 }}>
                        <Building2 size={18} style={{ verticalAlign: "middle", marginRight: 8 }} />
                        Department Breakdown
                    </h3>
                    <table className="data-table">
                        <thead>
                            <tr>
                                <th className="text-left">Department</th>
                                <th className="text-right">People</th>
                                <th className="text-right">Draft Hours</th>
                                <th className="text-right">Billable</th>
                                <th className="text-right">Entries</th>
                            </tr>
                        </thead>
                        <tbody>
                            {deptSummaries.map((d) => (
                                <tr key={d.department}>
                                    <td className="cell-primary">{d.department}</td>
                                    <td className="text-right cell-secondary">{d.headcount}</td>
                                    <td className="text-right cell-value" style={{ fontWeight: 600 }}>{d.draftHours.toFixed(1)}</td>
                                    <td className="text-right cell-value accent">{d.billableHours.toFixed(1)}</td>
                                    <td className="text-right cell-secondary">{d.entryCount}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {/* Main Data Table */}
            <div className="data-table-container">
                <DataTable
                    columns={columns}
                    data={filteredData}
                    customStyles={customStyles}
                    pagination
                    paginationPerPage={25}
                    paginationRowsPerPageOptions={[10, 25, 50, 100]}
                    highlightOnHover
                    defaultSortFieldId={3}
                    defaultSortAsc={false}
                    expandableRows
                    expandableRowsComponent={ExpandedEntries}
                    noDataComponent={
                        <div style={{ padding: 40, textAlign: "center", color: "var(--text-muted)" }}>
                            {rawEntries.length === 0
                                ? "No draft time entries found for the selected date range."
                                : "No results match the current filters."}
                        </div>
                    }
                />
            </div>
        </div>
    );
};

export default DraftTimekeeping;
